/**
 * Auth context.
 *
 * The role comes from the `user_role` claim baked into the JWT by the Custom
 * Access Token Hook (migration 0200) — not from a table lookup. Two reasons:
 *
 *   1. It cannot be forged. The claim is written server-side before Supabase
 *      signs the token; the client never touches that path.
 *   2. It costs nothing to read. No round trip per role check.
 *
 * The catch, and it's a real one: claims are frozen at token issuance. Promote
 * a farmer to superadmin and their app keeps the old role until the token
 * refreshes (up to an hour). `refreshRole()` exists for exactly that moment —
 * call it right after any role change.
 *
 * Note this is UI-level gating only. It decides which buttons render. It is
 * NOT the security boundary — RLS is. A user who forges a role in devtools
 * gets a broken-looking admin screen whose every query returns nothing.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import type { AppRole } from '@sreerasa/shared';
import { supabase } from '@/lib/supabase';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isFarmer: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
  /** Force a token refresh so a just-changed role takes effect now. */
  refreshRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Reads the role claim out of the access token.
 *
 * No signature verification here, and none needed: this token came from
 * Supabase's own session store, and every request it authenticates is
 * verified server-side anyway. We are reading it to decide what to render,
 * not to decide what to permit.
 */
function roleFromToken(accessToken: string | undefined): AppRole | null {
  if (!accessToken) return null;
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;

    const normalised = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalised.padEnd(normalised.length + ((4 - (normalised.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as { user_role?: AppRole | null };

    return claims.user_role ?? null;
  } catch {
    // A malformed token means no role, never a default role. Fail closed.
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setIsLoading(false);
    });

    // Fires on sign-in, sign-out, and every token refresh — so the role stays
    // current without polling.
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return;
      setSession(nextSession);
      setIsLoading(false);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const role = useMemo(() => roleFromToken(session?.access_token), [session?.access_token]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const refreshRole = useCallback(async () => {
    const { data } = await supabase.auth.refreshSession();
    setSession(data.session);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      session,
      role,
      isLoading,
      isAuthenticated: Boolean(session?.user),
      // A superadmin can do everything a farmer can, so treat them as one for
      // rendering purposes — otherwise every farmer surface needs two checks.
      isFarmer: role === 'farmer' || role === 'superadmin',
      isAdmin: role === 'superadmin',
      signOut,
      refreshRole,
    }),
    [session, role, isLoading, signOut, refreshRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return context;
}
